import { useEffect, useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAppStore } from '../stores/app';
import { useClientTimer } from '../hooks/useTimer';
import { connectSocket } from '../lib/socket';
import { Timer } from '../components/player/Timer';

export function TVModePage() {
  const store = useAppStore();
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<any>(null);
  const anonIdRef = useRef('anon_' + Math.random().toString(36).slice(2));

  useEffect(() => {
    const anonId = anonIdRef.current;
    store.setAuth(anonId as any, 'Экран', anonId);
    store.setRole('screen');

    const sock = connectSocket({
      token: anonId,
      role: 'screen',
    });

    socketRef.current = sock;

    const onConnect = () => {
      console.log('TV Mode connected');
      setIsConnected(true);
    };

    if (sock.connected) {
      onConnect();
    } else {
      sock.on('connect', onConnect);
    }

    sock.on('room:player_joined', (data: any) => {
      console.log('room:player_joined', data);
      store.addPlayer(data.player);
    });

    sock.on('room:player_left', (data: any) => {
      console.log('room:player_left', data);
      store.removePlayer(data.playerId);
    });

    sock.on('room:player_ready', (data: any) => {
      console.log('room:player_ready', data);
      store.updatePlayerReady(data.playerId, data.isReady);
    });

    sock.on('game:preparing', () => {
      console.log('game:preparing');
      store.setGameStatus('preparing');
    });

    sock.on('game:started', (data: any) => {
      console.log('game:started', data);
      store.setGameStarted(data.gameId, data.totalQuestions);
    });

    sock.on('game:countdown', (data: any) => {
      console.log('game:countdown', data);
      store.setCountdown(data.seconds);
    });

    sock.on('game:question', (data: any) => {
      console.log('game:question', data);
      store.setQuestion({
        questionId: data.question?.id || '',
        questionText: data.question?.text || '',
        options: data.question?.options || [],
        round: data.round,
      });
    });

    sock.on('game:round_results', (data: any) => {
      console.log('game:round_results', data);
      store.setRoundResults(data, anonId as any);
    });

    sock.on('game:finished', (data: any) => {
      console.log('game:finished', data);
      store.setGameFinished(data.leaderboard || []);
    });

  }, []);

  useClientTimer();

  useEffect(() => {
    if (isConnected && !store.room && socketRef.current) {
      socketRef.current.emit('room:create', {}, (response: any) => {
        console.log('room:create response:', response);
        if ('room' in response) {
          store.setRoom(response.room);
          store.setScreen('lobby');
        } else if ('error' in response) {
          console.error('room:create error:', response.error);
        }
      });
    }
  }, [isConnected, store.room]);

  const room = store.room;
  const gameStatus = store.gameStatus;

  if (!room) {
    return (
      <div className="tv-mode flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">🎮</div>
          <p className="text-2xl">Создаём комнату...</p>
        </div>
      </div>
    );
  }

  const telegramUrl = 'https://t.me/qwizmug_bot?start=' + room.code;

  if (gameStatus === 'idle') {
    return (
      <div className="tv-mode flex flex-col p-6 h-screen max-h-screen overflow-hidden">
        <header className="flex justify-between items-center mb-4 flex-shrink-0">
          <h1 className="text-3xl font-bold">🎮 BESHEK</h1>
          <div className="text-right">
            <div className="text-sm text-gray-400">Комната</div>
            <div className="text-4xl font-bold tracking-wider text-primary-400">{room.code}</div>
          </div>
        </header>
        <main className="flex-1 flex gap-8 min-h-0">
          <section className="flex-shrink-0 flex flex-col items-center">
            <div className="qr-container">
              <QRCodeSVG value={telegramUrl} size={220} />
            </div>
            <p className="text-center mt-3 text-gray-400 text-sm">Отсканируй камерой</p>
          </section>
          <section className="flex-1 min-h-0 overflow-auto">
            <h2 className="text-xl font-semibold mb-3">
              Игроки ({room.players.length}/16)
            </h2>
            <div className="grid grid-cols-4 gap-3">
              {room.players.map((player) => (
                <div key={player.id} className={'p-3 rounded-xl text-center ' + (player.isReady ? 'bg-green-500/20 border-2 border-green-500' : 'bg-white/10')}>
                  <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-1">
                    {player.avatar || '🎮'}
                  </div>
                  <div className="font-medium truncate text-sm">{player.displayName}</div>
                  <div className="text-xs text-gray-400">{player.isReady ? '✓ Готов' : 'Ожидание'}</div>
                </div>
              ))}
              {Array.from({ length: Math.min(8, 16 - room.players.length) }).map((_, i) => (
                <div key={'empty-' + i} className="p-3 rounded-xl bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center text-gray-600">?</div>
              ))}
            </div>
          </section>
        </main>
        <footer className="mt-4 text-center text-gray-400 flex-shrink-0">
          {room.players.length < 2 ? <p className="text-lg">Ждём игроков...</p> : <p className="text-lg">Готовы: {room.players.filter(p => p.isReady).length}/{room.players.length} — Ведущий может начать игру</p>}
        </footer>
      </div>
    );
  }

  if (gameStatus === 'preparing' || gameStatus === 'countdown') {
    return (
      <div className="tv-mode flex items-center justify-center h-screen">
        <div className="text-center">
          {gameStatus === 'preparing' ? (
            <>
              <div className="animate-pulse text-7xl mb-4">🎲</div>
              <p className="text-2xl">Готовим вопросы...</p>
            </>
          ) : (
            <>
              <div className="text-[150px] font-bold animate-bounce leading-none">{Math.ceil(store.timeLeft / 1000)}</div>
              <p className="text-2xl text-gray-400 mt-2">Приготовьтесь!</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (gameStatus === 'question') {
    const options = store.options;
    const COLORS = ['bg-game-red', 'bg-game-blue', 'bg-game-yellow', 'bg-game-green'];
    const MARKERS = ['▲', '●', '■', '◆'];
    return (
      <div className="tv-mode flex flex-col p-4 h-screen max-h-screen overflow-hidden">
        <header className="flex justify-between items-center mb-2 flex-shrink-0">
          <div className="text-lg text-gray-400">Вопрос {store.currentRound} из {store.totalQuestions}</div>
          <Timer />
        </header>
        <main className="flex-1 flex flex-col items-center justify-center min-h-0">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 max-w-5xl px-4 leading-tight">{store.questionText || 'Смотрите вопрос...'}</h2>
          <div className="grid grid-cols-2 gap-3 w-full max-w-4xl px-4">
            {options.map((option, index) => (
              <div key={index} className={'p-4 rounded-xl text-white text-lg md:text-xl font-semibold flex items-center ' + COLORS[index] + (index === 2 ? ' text-black' : '')}>
                <span className="mr-3 opacity-70 text-xl">{MARKERS[index]}</span>
                <span className="leading-tight">{option}</span>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (gameStatus === 'results') {
    const results = store.roundResults;
    const COLORS = ['bg-game-red', 'bg-game-blue', 'bg-game-yellow', 'bg-game-green'];
    return (
      <div className="tv-mode flex flex-col p-4 h-screen max-h-screen overflow-hidden">
        <header className="text-center mb-4 flex-shrink-0">
          <h2 className="text-2xl font-bold mb-2 leading-tight">{results?.question.text}</h2>
          <div className={'inline-block px-4 py-2 rounded-xl text-xl font-bold ' + COLORS[results?.question.correctIndex || 0] + (results?.question.correctIndex === 2 ? ' text-black' : ' text-white')}>
            ✓ {results?.question.options[results?.question.correctIndex || 0]}
          </div>
          {results?.question.explanation && <p className="mt-2 text-gray-400 text-sm max-w-2xl mx-auto">{results.question.explanation}</p>}
        </header>
        <div className="flex justify-center gap-6 mb-4 flex-shrink-0">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-400">{results?.stats.correctCount}</div>
            <div className="text-gray-400 text-sm">правильных</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{results?.stats.totalAnswered}</div>
            <div className="text-gray-400 text-sm">ответили</div>
          </div>
        </div>
        <div className="flex-1 max-w-2xl mx-auto w-full min-h-0 overflow-auto">
          <h3 className="text-lg font-semibold mb-2 text-center">Таблица лидеров</h3>
          <div className="space-y-2">
            {store.leaderboard.slice(0, 5).map((entry) => (
              <div key={entry.playerId} className="flex items-center justify-between p-3 bg-white/10 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-xl w-8">{entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank + '.'}</span>
                  <span className="font-medium">{entry.avatar || "🎮"} {entry.displayName}</span>
                </div>
                <div className="text-xl font-bold">{entry.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (gameStatus === 'finished') {
    const winner = store.leaderboard[0];
    return (
      <div className="tv-mode flex flex-col items-center justify-center p-4 h-screen">
        <div className="text-center mb-6">
          <div className="text-6xl mb-2">🏆</div>
          <h1 className="text-4xl font-bold mb-2">Победитель!</h1>
          <div className="text-4xl font-bold text-primary-400">{winner?.avatar || "🎮"} {winner?.displayName}</div>
          <div className="text-2xl text-gray-400 mt-1">{winner?.score} очков</div>
        </div>
        <div className="w-full max-w-2xl overflow-auto">
          <div className="space-y-2">
            {store.leaderboard.map((entry) => (
              <div key={entry.playerId} className={'flex items-center justify-between p-3 rounded-xl ' + (entry.rank === 1 ? 'bg-yellow-500/20 border-2 border-yellow-500' : 'bg-white/10')}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl w-10">{entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank + '.'}</span>
                  <span className="font-medium text-lg">{entry.avatar || "🎮"} {entry.displayName}</span>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{entry.score}</div>
                  <div className="text-xs text-gray-400">{entry.correctAnswers} верных</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}